/**
 * The wire contract. Every callable validates against one of these.
 *
 * All object schemas are `.strict()`: an unknown key is a rejected request, not
 * a silently-dropped one. That is what stops a future client edit from
 * accidentally widening what reaches the server.
 */

import { z } from 'zod';
import {
  ANCHOR_STATUSES,
  BACKUP_CONDITIONS,
  DEVICE_PLATFORMS,
  DRILL_OUTCOMES,
  DRILL_TRACKS,
  FIND_TIME_BUCKETS,
  LOCAL_VAULT_STATUSES,
  NOTIFY_WINDOWS,
  PHYSICAL_VERIFICATION_TYPES,
} from './enums.js';
import { MAX_CADENCE_DAYS, MIN_CADENCE_DAYS } from './scoring.js';

/** Lowercase hex SHA-256. */
export const sha256Hex = z
  .string()
  .regex(/^[0-9a-f]{64}$/, 'must be lowercase hex sha-256');

/** 16 bytes of salt, hex. */
export const saltHex = z.string().regex(/^[0-9a-f]{32}$/, 'must be 16 hex bytes');

export const firestoreId = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[A-Za-z0-9_-]+$/, 'must be an opaque id');

export const idempotencyKey = z
  .string()
  .min(8)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/);

/** IANA timezone, loosely shaped. Resolved strictly server-side. */
export const timezone = z.string().min(1).max(64).regex(/^[A-Za-z0-9_+\-/]+$/);
export const locale = z.string().min(2).max(12).regex(/^[a-zA-Z-]+$/);

export const cadenceDays = z
  .number()
  .int()
  .min(MIN_CADENCE_DAYS)
  .max(MAX_CADENCE_DAYS);

export const hintDepthUsed = z.number().int().min(0).max(5);

// ---------------------------------------------------------------------------
// Callable inputs
// ---------------------------------------------------------------------------

/**
 * First sign-in. Creates the user document server-side so email comes from the
 * auth token and the calendar token comes from a server CSPRNG — the rules deny
 * client creation of user documents outright.
 */
export const bootstrapUserInput = z
  .object({
    timezone,
    locale,
  })
  .strict();
export type BootstrapUserInput = z.infer<typeof bootstrapUserInput>;

export const createAnchorInput = z
  .object({
    cadenceDays,
    recoveryCodeHash: sha256Hex,
    salt: saltHex,
  })
  .strict();
export type CreateAnchorInput = z.infer<typeof createAnchorInput>;

export const startDrillInput = z
  .object({
    drillId: firestoreId,
  })
  .strict();
export type StartDrillInput = z.infer<typeof startDrillInput>;

export const completeDrillInput = z
  .object({
    drillId: firestoreId,
    idempotencyKey,
    outcome: z.enum(DRILL_OUTCOMES),
    hintDepthUsed,
    sequencePassed: z.boolean(),
    physicalVerificationType: z.enum(PHYSICAL_VERIFICATION_TYPES),
    findTimeBucket: z.enum(FIND_TIME_BUCKETS),
    backupCondition: z.enum(BACKUP_CONDITIONS),
    /**
     * Present only when the user actually read the code off the paper.
     * A mismatch is *not* an error — it downgrades verification to `self`.
     */
    recoveryCodeHash: sha256Hex.optional(),
  })
  .strict();
export type CompleteDrillInput = z.infer<typeof completeDrillInput>;

export const snoozeDrillInput = z
  .object({
    drillId: firestoreId,
    days: z.number().int().min(1).max(7),
  })
  .strict();
export type SnoozeDrillInput = z.infer<typeof snoozeDrillInput>;

export const registerDeviceInput = z
  .object({
    fcmToken: z.string().min(16).max(4096),
    platform: z.enum(DEVICE_PLATFORMS),
    pushEnabled: z.boolean(),
  })
  .strict();
export type RegisterDeviceInput = z.infer<typeof registerDeviceInput>;

export const updateAnchorScheduleInput = z
  .object({
    anchorId: firestoreId,
    cadenceDays: cadenceDays.optional(),
    status: z.enum(ANCHOR_STATUSES).exclude(['archived']).optional(),
  })
  .strict();
export type UpdateAnchorScheduleInput = z.infer<typeof updateAnchorScheduleInput>;

export const rotateRecoveryCodeInput = z
  .object({
    anchorId: firestoreId,
    recoveryCodeHash: sha256Hex,
    salt: saltHex,
  })
  .strict();
export type RotateRecoveryCodeInput = z.infer<typeof rotateRecoveryCodeInput>;

export const rotateCalendarTokenInput = z.object({}).strict();

export const deleteAnchorInput = z
  .object({
    anchorId: firestoreId,
  })
  .strict();
export type DeleteAnchorInput = z.infer<typeof deleteAnchorInput>;

export const deleteAccountInput = z
  .object({
    /** Typed confirmation, matched literally. Guards against a stray call. */
    confirm: z.literal('DELETE'),
  })
  .strict();
export type DeleteAccountInput = z.infer<typeof deleteAccountInput>;

/** The only user fields a client may propose. Everything else is server-owned. */
export const updateProfileInput = z
  .object({
    timezone: timezone.optional(),
    locale: locale.optional(),
    preferredWindow: z.enum(NOTIFY_WINDOWS).optional(),
    localVaultStatus: z.enum(LOCAL_VAULT_STATUSES).optional(),
    persistGranted: z.boolean().optional(),
    installedAsPWA: z.boolean().optional(),
  })
  .strict();
export type UpdateProfileInput = z.infer<typeof updateProfileInput>;

/** The heartbeat the app fires on every launch. */
export const vaultHeartbeatInput = z
  .object({
    localVaultStatus: z.enum(LOCAL_VAULT_STATUSES),
    persistGranted: z.boolean(),
    installedAsPWA: z.boolean(),
  })
  .strict();
export type VaultHeartbeatInput = z.infer<typeof vaultHeartbeatInput>;

// ---------------------------------------------------------------------------
// Read models (what the client receives back)
// ---------------------------------------------------------------------------

export const anchorView = z.object({
  id: firestoreId,
  status: z.enum(ANCHOR_STATUSES),
  cadenceDays,
  nextDueAt: z.number(),
  lastDrillAt: z.number().nullable(),
  lastPhysicalVerifiedAt: z.number().nullable(),
  physicalDueAt: z.number(),
  recallScore: z.number(),
  readinessScore: z.number(),
  createdAt: z.number(),
});
export type AnchorView = z.infer<typeof anchorView>;

export const drillView = z.object({
  id: firestoreId,
  anchorId: firestoreId,
  track: z.enum(DRILL_TRACKS),
  scheduledFor: z.number(),
  startedAt: z.number().nullable(),
  completedAt: z.number().nullable(),
  snoozeCount: z.number().int(),
});
export type DrillView = z.infer<typeof drillView>;
